import type { ReactNode } from 'react';
import { cn } from '@/utils/classnames';
import { MaterialIcon } from './MaterialIcon';
import { JsonView } from './JsonView';

export interface ErrorCardProps {
  title?: ReactNode;
  /** Status code shown as a small chip in the header. */
  status?: number;
  /** Free-form summary message; defaults to nothing. */
  message?: ReactNode;
  /** Raw response body (object / string). Rendered with JsonView when an object. */
  body?: unknown;
  /** Optional retry callback — renders a button when provided. */
  onRetry?: () => void;
  className?: string;
}

export function ErrorCard({ title, status, message, body, onRetry, className }: ErrorCardProps) {
  return (
    <div
      className={cn(
        'rounded-lg border border-error/40 bg-error-container/30 p-container-padding',
        className,
      )}
    >
      <div className="flex items-start gap-stack-sm">
        <span className="text-error">
          <MaterialIcon name="error" size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-stack-sm">
            <h3 className="font-headline-md text-headline-md text-on-error-container">
              {title ?? '请求失败'}
            </h3>
            {typeof status === 'number' && status > 0 ? (
              <span className="font-label-caps text-label-caps uppercase rounded bg-error-container px-stack-sm py-unit text-on-error-container">
                HTTP {status}
              </span>
            ) : null}
          </div>
          {message ? (
            <p className="mt-unit text-body-sm text-on-error-container">{message}</p>
          ) : null}
        </div>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-unit rounded bg-error px-stack-sm py-unit font-label-caps text-label-caps uppercase text-on-error hover:bg-error/90"
          >
            <MaterialIcon name="refresh" size={14} />
            重试
          </button>
        ) : null}
      </div>
      {body !== undefined && body !== null ? (
        <div className="mt-stack-md">
          {typeof body === 'string' ? (
            <pre className="overflow-x-auto rounded bg-[#1E1E1E] px-stack-md py-stack-sm font-code-md text-code-md text-[#E5E7EB]">
              {body}
            </pre>
          ) : (
            <JsonView value={body} />
          )}
        </div>
      ) : null}
    </div>
  );
}
